import React, { useState, useEffect, useCallback } from 'react';
import {
  Truck, Fuel, Wrench, FileText, Search, Download, ArrowUpRight,
  ChevronRight, ExternalLink
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function FleetCosts() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadCosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/fleet-costs', { params });
      const raw = res.data;
      const list = raw?.data || raw?.items || (Array.isArray(raw) ? raw : []);
      setVehicles(list);
    } catch (err) {
      console.error('Erro ao carregar custos da frota', err);
      toast.error('Erro ao carregar custos por veículo');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  const filtered = vehicles.filter(v => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (v.plate || '').toLowerCase().includes(s) ||
      (v.fleet_number || '').toLowerCase().includes(s) ||
      (v.model || '').toLowerCase().includes(s)
    );
  });

  // KPIs
  const totalFleetCost = filtered.reduce((acc, v) => acc + (v.total_cost || 0), 0);
  const totalMaintenance = filtered.reduce((acc, v) => acc + (v.maintenance_cost || 0), 0);
  const totalFuel = filtered.reduce((acc, v) => acc + (v.fuel_cost || 0), 0);
  const avgCostPerVehicle = filtered.length > 0 ? totalFleetCost / filtered.length : 0;

  // Chart data: Top 8 vehicles with highest total cost
  const chartData = [...filtered]
    .sort((a, b) => b.total_cost - a.total_cost)
    .slice(0, 8)
    .map(v => ({
      name: v.plate || v.fleet_number || 'Veículo',
      manutencao: v.maintenance_cost,
      combustivel: v.fuel_cost,
      licencas: v.licenses_cost,
    }));

  const handleExportCSV = () => {
    if (!filtered.length) return;
    const headers = ['Placa', 'Prefixo', 'Modelo', 'Custo Manutenção', 'Custo Combustível', 'Licenças/Doc', 'Custo Extraordinário', 'Custo Total'];
    const rows = filtered.map(v => [
      v.plate || '',
      v.fleet_number || '',
      `"${(v.model || '').replace(/"/g, '""')}"`,
      v.maintenance_cost?.toFixed(2) || '0.00',
      v.fuel_cost?.toFixed(2) || '0.00',
      v.licenses_cost?.toFixed(2) || '0.00',
      v.extraordinary_cost?.toFixed(2) || '0.00',
      v.total_cost?.toFixed(2) || '0.00'
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'custos_frota_por_veiculo.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Relatório exportado com sucesso');
  };

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Custos da Frota"
        subtitle="Apuração consolidada e individual de custos de manutenção, combustível e licenciamento por veículo"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onRefresh={loadCosts}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Custo Total da Frota</p>
          <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
            {formatCurrency(totalFleetCost)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{filtered.length} veículos analisados</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Custo Médio / Veículo</p>
          <p className="text-2xl font-bold mt-1 text-neutral-900 dark:text-neutral-100">
            {formatCurrency(avgCostPerVehicle)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">Média operacional</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Total em Combustível</p>
          <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400">
            {formatCurrency(totalFuel)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{totalFleetCost > 0 ? `${((totalFuel / totalFleetCost) * 100).toFixed(1)}% dos custos` : '-'}</p>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
          <p className="text-xs uppercase font-semibold text-neutral-500">Total em Manutenção (OS)</p>
          <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
            {formatCurrency(totalMaintenance)}
          </p>
          <p className="text-xs text-neutral-400 mt-0.5">{totalFleetCost > 0 ? `${((totalMaintenance / totalFleetCost) * 100).toFixed(1)}% dos custos` : '-'}</p>
        </div>
      </div>

      {/* Gráfico Ranking de Veículos com Maior Custo */}
      {chartData.length > 0 && (
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <div className="mb-4">
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Top 8 Veículos com Maior Custo Operacional</h3>
            <p className="text-xs text-neutral-500">Detalhamento dos custos por combustível, manutenção e taxas</p>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="name" fontSize={11} stroke="#888" />
                <YAxis fontSize={11} stroke="#888" tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [formatCurrency(v)]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="combustivel" name="Combustível" stackId="a" fill="#F59E0B" />
                <Bar dataKey="manutencao" name="Manutenção (OS)" stackId="a" fill="#3B82F6" />
                <Bar dataKey="licencas" name="Licenças/Doc" stackId="a" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Buscar por placa, prefixo ou modelo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="h-9 text-xs gap-1.5"
        >
          <Download className="h-3.5 w-3.5" />
          Exportar Relatório
        </Button>
      </div>

      {/* Tabela de Custos por Veículo */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="py-3 px-4">Veículo / Placa</th>
                <th className="py-3 px-4">Modelo</th>
                <th className="py-3 px-4 text-right">Manutenção (OS)</th>
                <th className="py-3 px-4 text-right">Combustível</th>
                <th className="py-3 px-4 text-right">Licenças / Doc</th>
                <th className="py-3 px-4 text-right">Extraordinários</th>
                <th className="py-3 px-4 text-right">Custo Total</th>
                <th className="py-3 px-4 text-center">Extrato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="py-4 px-4">
                      <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-neutral-400">
                    Nenhum veículo encontrado com os filtros selecionados.
                  </td>
                </tr>
              ) : (
                filtered.map((v) => (
                  <tr key={v.vehicle_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                    <td className="py-3 px-4 font-bold text-neutral-900 dark:text-neutral-100">
                      🚌 {v.plate}
                      {v.fleet_number && (
                        <span className="ml-1.5 font-normal text-neutral-500 text-[11px]">
                          (Prefixo: {v.fleet_number})
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-neutral-700 dark:text-neutral-300">
                      {v.model || 'Não informado'}
                    </td>
                    <td className="py-3 px-4 text-right text-blue-600 dark:text-blue-400 font-medium">
                      {v.maintenance_cost > 0 ? formatCurrency(v.maintenance_cost) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-amber-600 dark:text-amber-400 font-medium">
                      {v.fuel_cost > 0 ? formatCurrency(v.fuel_cost) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-emerald-600 dark:text-emerald-400 font-medium">
                      {v.licenses_cost > 0 ? formatCurrency(v.licenses_cost) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right text-red-500 font-medium">
                      {v.extraordinary_cost > 0 ? formatCurrency(v.extraordinary_cost) : '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(v.total_cost)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Link
                        to={`/financeiro/lancamentos?search=${v.plate}`}
                        className="inline-flex items-center gap-1 text-xs text-brand hover:underline font-semibold"
                      >
                        Ver Lançamentos <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
